import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { Tooltip, Input, Space, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { IGiveawayAutoParticipant } from "../../../../../../interfaces";
import { EmitError } from "../../../../../../utils/EmitError";
import { poeValorComPontosEmMil } from "../../../../../../utils/Conversores";
import { formatCpfCnpj, onlyNumbers } from "../../../../../../utils/Masks";
import GiveawaysAutoService from "../../../../../../services/http/services/GiveawaysAutoService";
import { ThemeContext } from "../../../../../../contexts/ThemeContext";
import { IActiveFilterItem } from "../../../../../../components/ActiveFiltersBar";
import TableActionButton from "../../../../../../components/TableActionButton";
import { getSortOrder } from "../../../../../../hooks/SortOrder";
import { getNextSortOrder } from "../../../../../../hooks/NextSortOrder";

export interface ITableData extends IGiveawayAutoParticipant {
    key: number | undefined;
    app_user_id: number;
    app_user_cpfCnpj: string;
    app_user_name: string;
}

export interface IParticipantAutoFilters {
    name: string | null;
    cpfCnpj: string | null;
}

export default function useGiveawayParticipants(giveawayId: number) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const { id } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawParticipants, setRawParticipants] = useState<Array<ITableData>>([]);
    const lastSorterRef = useRef<{ field: string; order: string } | any>({ field: 'app_user_name', order: 'asc' });

    const [filters, setFilters] = useState<IParticipantAutoFilters>({
        name: null,
        cpfCnpj: null,
    });

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
    });

    const [sorter, setSorter] = useState<{ field: string; order: 'asc' | 'desc' }>({
        field: 'app_user_name',
        order: 'asc',
    });

    const [searchName, setSearchName] = useState('');
    const [searchCpfCnpj, setSearchCpfCnpj] = useState('');

    const nameInputRef = useRef<any>(null);

    const handleFilterClear = useCallback((field: keyof IParticipantAutoFilters) => {
        setFilters((prev) => ({ ...prev, [field]: null }));
        if (field === 'name') setSearchName('');
        if (field === 'cpfCnpj') setSearchCpfCnpj('');
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({ name: null, cpfCnpj: null });
        setSearchName('');
        setSearchCpfCnpj('');
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const activeFilterItems: Array<IActiveFilterItem | null> = [
        filters.name
            ? { key: 'name', label: 'Nome', value: filters.name, onClear: () => handleFilterClear('name') }
            : null,
        filters.cpfCnpj
            ? { key: 'cpfCnpj', label: 'CPF/CNPJ', value: formatCpfCnpj(filters.cpfCnpj), onClear: () => handleFilterClear('cpfCnpj') }
            : null,
    ];

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const handleTableChange = useCallback((
        _pag: any,
        _filts: any,
        sorterObjParam: any
    ) => {
        const sorterObj = Array.isArray(sorterObjParam) ? sorterObjParam[0] : sorterObjParam;
        const { nextSort, nextOrder } = getNextSortOrder(
            sorterObj,
            { pagination, filters, sorter },
            pagination.current,
            lastSorterRef
        );

        if (nextSort && nextOrder) {
            setSorter({ field: nextSort, order: nextOrder as 'asc' | 'desc' });
        }
    }, [pagination, filters, sorter]);

    const filteredData = useMemo(() => {
        let result = [...rawParticipants];

        if (filters.name) {
            const term = filters.name.toLowerCase();
            result = result.filter((item) => item.app_user_name?.toLowerCase().includes(term));
        }

        if (filters.cpfCnpj) {
            const raw = onlyNumbers(filters.cpfCnpj);
            result = result.filter((item) => onlyNumbers(item.app_user_cpfCnpj).includes(raw));
        }

        if (sorter.field) {
            result.sort((a: any, b: any) => {
                const valA = a[sorter.field];
                const valB = b[sorter.field];

                if (typeof valA === 'number' && typeof valB === 'number') {
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }

                const strA = (valA || '').toString();
                const strB = (valB || '').toString();
                return sorter.order === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
            });
        }

        return result;
    }, [rawParticipants, filters, sorter]);

    const paginatedData = useMemo(() => {
        const start = (pagination.current - 1) * pagination.pageSize;
        return filteredData.slice(start, start + pagination.pageSize);
    }, [filteredData, pagination]);

    const columns = [
        {
            title: 'Nome / Razão Social',
            dataIndex: 'app_user_name',
            key: 'app_user_name',
            sorter: true,
            showSorterTooltip: false,
            sortOrder: getSortOrder('app_user_name', sorter),
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            Nome
                        </div>
                        <Input
                            ref={nameInputRef}
                            placeholder="Buscar por nome..."
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            onPressEnter={() => {
                                confirm();
                                setFilters((prev) => ({ ...prev, name: searchName || null, cpfCnpj: searchCpfCnpj || null }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            allowClear
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            CPF / CNPJ
                        </div>
                        <Input
                            placeholder="000.000.000-00"
                            value={searchCpfCnpj}
                            onChange={(e) => setSearchCpfCnpj(formatCpfCnpj(e.target.value))}
                            onPressEnter={() => {
                                confirm();
                                setFilters((prev) => ({ ...prev, name: searchName || null, cpfCnpj: searchCpfCnpj || null }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            allowClear
                        />
                    </div>
                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                setFilters((prev) => ({ ...prev, name: searchName || null, cpfCnpj: searchCpfCnpj || null }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchName('');
                                setSearchCpfCnpj('');
                                if (clearFilters) clearFilters();
                                confirm();
                                setFilters((prev) => ({ ...prev, name: null, cpfCnpj: null }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: (filters.name || filters.cpfCnpj) ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => nameInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 13, lineHeight: '1.3' }}>
                        {filters.name ? (
                            <Highlighter
                                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                searchWords={[filters.name]}
                                autoEscape
                                textToHighlight={record.app_user_name || '--'}
                            />
                        ) : (
                            record.app_user_name || '--'
                        )}
                    </span>
                    <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 12, fontWeight: 500, letterSpacing: '0.3px', whiteSpace: 'nowrap', lineHeight: '1.2' }}>
                        {filters.cpfCnpj ? (
                            <Highlighter
                                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                searchWords={[filters.cpfCnpj]}
                                autoEscape
                                textToHighlight={record.app_user_cpfCnpj ? formatCpfCnpj(record.app_user_cpfCnpj) : '--'}
                            />
                        ) : (
                            record.app_user_cpfCnpj ? formatCpfCnpj(record.app_user_cpfCnpj) : '--'
                        )}
                    </span>
                </div>
            ),
            width: '45%'
        },
        {
            title: 'Bilhetes',
            dataIndex: 'total_tickets',
            key: 'total_tickets',
            sorter: true,
            showSorterTooltip: false,
            sortOrder: getSortOrder('total_tickets', sorter),
            align: 'center' as const,
            render: (value?: number) => (
                <Tooltip color="#1677ff" title="Bilhetes gerados neste sorteio">
                    <span style={{ fontWeight: 700, color: '#1677ff', fontSize: 14 }}>
                        {poeValorComPontosEmMil(value) ?? '0'}
                    </span>
                </Tooltip>
            ),
            width: '35%'
        },
        {
            title: 'Ações',
            key: 'actions',
            width: '10%',
            align: 'center' as const,
            render: (_: any, record: ITableData) => (
                <TableActionButton
                    action="view"
                    tooltip="Ver bilhetes do participante"
                    onClick={() => navigate(`/usuarios/auto/${record.app_user_id}/bilhetes`)}
                />
            )
        },
    ];

    const setTableDataFromRequest = useCallback((data: IGiveawayAutoParticipant[]) => {
        if (!data) return;
        const newData = data.map((participant) => ({
            ...participant,
            key: participant.id,
            app_user_id: participant?.id!,
            app_user_name: participant?.name!,
            app_user_cpfCnpj: participant?.cpf_cnpj!,
        }));
        setRawParticipants(newData);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function loadParticipants() {
            try {
                setIsLoading(true);
                const giveawayParticipantsData = await GiveawaysAutoService.listGiveawayParticipantsById({
                    id: Number(id || giveawayId),
                    signal: controller.signal
                });

                setTableDataFromRequest(giveawayParticipantsData);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'giveawayParticipants_aosdpasdpasd');
                setIsLoading(false);
            }
        }

        loadParticipants();

        return () => controller.abort();
    }, [setTableDataFromRequest, id, giveawayId]);

    return {
        columns,
        isLoading,
        tableData: paginatedData,
        totalParticipants: filteredData.length,
        pagination,
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        navigate,
    };
}
